import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as cognitoAuth from '../utils/cognitoAuth';
import { IDLE_TIMEOUT_LAST_ACTIVITY_KEY } from '../hooks/useIdleTimeout';
import {
  AUTH_LOGOUT_CHANNEL,
  CROSS_ORIGIN_LOGOUT_REDIRECT_DELAY_MS,
  fanOutCrossOriginLogout,
} from '../utils/logoutSync';

// CJIS §5.10.1.3 — tokens and user identity must not persist to localStorage or sessionStorage.
// Module-level variables hold session state in memory only; all are cleared on page reload.
let _platformRefreshTokenMemory = null;
let _platformDeptMemory = null;
let _platformOrgModulesMemory = null;

// Test-only reset — keeps module state isolated between test cases.
export function _resetInMemoryTokensForTesting() {
  _platformRefreshTokenMemory = null;
  _platformDeptMemory = null;
  _platformOrgModulesMemory = null;
}

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

function readPlatformTokensFromHash() {
  const hash = new URLSearchParams(window.location.hash.slice(1));
  const idToken = hash.get('platform_id_token');
  if (!idToken) return null;
  return {
    idToken,
    accessToken: hash.get('platform_access_token'),
    refreshToken: hash.get('platform_refresh_token'),
    dept: hash.get('platform_dept') || null,
    orgModules: hash.get('platform_org_modules') || null,
  };
}

// CJIS §5.10.1.3: purge any keys persisted by prior non-compliant versions.
const LEGACY_KEYS = ['idToken', 'accessToken', 'platform_refresh_token', 'platform_sso', 'platform_dept', 'user'];

function purgeLegacyKeys() {
  LEGACY_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [idToken, setIdToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [isPlatformSso, setIsPlatformSso] = useState(false);
  const [platformDept, setPlatformDept] = useState(null);
  const [platformOrgModules, setPlatformOrgModules] = useState(null);
  const [loading, setLoading] = useState(true);
  // True once logout() starts, never reset — suppresses ProtectedRoute's own
  // redirect so it doesn't race the deferred one below.
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Tracks the latest user without re-subscribing the logout listener; lets the
  // listener ignore broadcasts in a tab that has no active session.
  const userRef = useRef(null);
  // Persistent same-origin logout channel (also used to notify other tabs).
  const logoutChannelRef = useRef(null);

  useEffect(() => { purgeLegacyKeys(); }, []);

  useEffect(() => { userRef.current = user; }, [user]);

  // Clears in-memory + SDK-persisted session state for THIS tab. No server call,
  // no redirect, no broadcast/fan-out — safe to reuse from both logout() and the
  // cross-tab logout listener without re-triggering the sync machinery.
  const clearLocalSession = useCallback(() => {
    _platformRefreshTokenMemory = null;
    _platformDeptMemory = null;
    _platformOrgModulesMemory = null;
    setUser(null);
    setIdToken(null);
    setAccessToken(null);
    setRefreshToken(null);
    setIsPlatformSso(false);
    setPlatformDept(null);
    setPlatformOrgModules(null);
    localStorage.removeItem('rememberMe');
  }, []);

  const redirectToLogin = useCallback((reason) => {
    const platformUrl = process.env.REACT_APP_PLATFORM_URL;
    const ALLOWED_REASONS = new Set(['session_timeout', 'session_expired']);
    const reasonSuffix = reason && ALLOWED_REASONS.has(reason) ? `?reason=${reason}` : '';
    window.location.replace(platformUrl ? `${platformUrl}/login${reasonSuffix}` : `/login${reasonSuffix}`);
  }, []);

  // Cross-product + cross-tab logout sync (no ticket — follow-up to CC-602).
  // A logout in another PC tab, or in a sibling product (via our /logout-bridge),
  // broadcasts here. BroadcastChannel never echoes to its own sender, so this
  // only fires in OTHER tabs. Guarded on userRef so a tab with no session (e.g.
  // sitting on /login) isn't force-redirected.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    const channel = new BroadcastChannel(AUTH_LOGOUT_CHANNEL);
    logoutChannelRef.current = channel;
    channel.onmessage = (event) => {
      if (event.data?.type === 'logout' && userRef.current) {
        clearLocalSession();
        redirectToLogin();
      }
    };
    return () => {
      channel.close();
      logoutChannelRef.current = null;
    };
  }, [clearLocalSession, redirectToLogin]);

  useEffect(() => {
    (async () => {
      // Platform SSO handoff — tokens arrive in URL hash from CC or platform
      const platform = readPlatformTokensFromHash();
      if (platform) {
        try {
          const part = platform.idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
          const padded = part + '='.repeat((4 - part.length % 4) % 4);
          const payload = JSON.parse(atob(padded));
          const userObj = cognitoAuth.extractUserFromPayload(payload);
          setUser(userObj);
          setIdToken(platform.idToken);
          if (platform.accessToken) {
            setAccessToken(platform.accessToken);
          }
          if (platform.refreshToken) {
            _platformRefreshTokenMemory = platform.refreshToken;
            setRefreshToken(platform.refreshToken);
            setIsPlatformSso(true);
          }
          if (platform.dept) {
            _platformDeptMemory = platform.dept;
            setPlatformDept(platform.dept);
          }
          if (platform.orgModules) {
            _platformOrgModulesMemory = platform.orgModules;
            setPlatformOrgModules(platform.orgModules);
          }
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          setLoading(false);
          return;
        } catch (e) {
          _platformRefreshTokenMemory = null;
          _platformDeptMemory = null;
          // eslint-disable-next-line no-console
          console.error('[CJIS] Platform SSO token decode failed — see server logs for details');
        }
      }

      // Restore platform SSO session from in-memory refresh token (lost on page reload by design)
      if (_platformRefreshTokenMemory) {
        try {
          const { user: u, tokens } = await cognitoAuth.refreshPlatformSession(_platformRefreshTokenMemory);
          setUser(u);
          setIdToken(tokens.idToken);
          setRefreshToken(_platformRefreshTokenMemory);
          setIsPlatformSso(true);
          if (_platformDeptMemory) setPlatformDept(_platformDeptMemory);
          if (_platformOrgModulesMemory) setPlatformOrgModules(_platformOrgModulesMemory);
          setLoading(false);
          return;
        } catch {
          _platformRefreshTokenMemory = null;
          _platformDeptMemory = null;
          _platformOrgModulesMemory = null;
          setUser(null);
          setIdToken(null);
          setAccessToken(null);
          setRefreshToken(null);
          setIsPlatformSso(false);
          setLoading(false);
          return;
        }
      }

      // Normal Cognito session restore
      try {
        const { user: u, tokens } = await cognitoAuth.getCurrentSession();
        setUser(u);
        setIdToken(tokens.idToken);
        setRefreshToken(tokens.refreshToken);
      } catch {
        _platformRefreshTokenMemory = null;
        localStorage.removeItem('rememberMe');
        setUser(null);
        setIdToken(null);
        setRefreshToken(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email, password, rememberMe) => {
    const { user: u, tokens } = await cognitoAuth.signIn(email, password);
    setUser(u);
    setIdToken(tokens.idToken);
    setRefreshToken(tokens.refreshToken);
    if (rememberMe) localStorage.setItem('rememberMe', 'true');
    return u;
  }, []);

  const logout = useCallback(async (reason) => {
    setIsLoggingOut(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/auth/logout`, {
        method: 'POST',
        signal: controller.signal,
        headers: { Authorization: `Bearer ${idToken}` },
      });
    } catch (e) {
      // Session termination audit record could not be written — log explicitly
      // so it surfaces in monitoring. Logout proceeds regardless (CJIS §5.4).
      const errType = e instanceof Error ? e.constructor.name : 'UnknownError';
      const isAbort = errType === 'AbortError';
      if (!isAbort) {
        // Non-abort failures mean the audit record was not written — flag prominently.
        console.error('[AUDIT] Session termination record failed to write:', errType, new Date().toISOString());
      }
    } finally {
      clearTimeout(timeoutId);
    }

    try {
      await cognitoAuth.signOut();
    } finally {
      // Notify other same-origin PC tabs to drop their session too. The
      // server-side revoke above already ran; other tabs just clear locally.
      logoutChannelRef.current?.postMessage({ type: 'logout' });
      clearLocalSession();
      // Stale timestamp would immediately re-trigger idle logout for the next login.
      localStorage.removeItem(IDLE_TIMEOUT_LAST_ACTIVITY_KEY);
      // Fan out to sibling products (CC, Platform) via hidden-iframe bridges.
      // Fire-and-forget: we don't await the network, but the redirect below is
      // deliberately deferred a beat so the iframes actually get to `load` and
      // postMessage before this tab navigates away (an immediate replace()
      // would abort the in-flight subframe requests and the sibling logout
      // fan-out would silently never fire).
      fanOutCrossOriginLogout();
      setTimeout(() => {
        redirectToLogin(reason);
        // Not resetting isLoggingOut: doing so re-armed ProtectedRoute's fallback
        // and fired a reason-clobbering second redirect on every normal logout.
      }, CROSS_ORIGIN_LOGOUT_REDIRECT_DELAY_MS);
    }

  }, [idToken, clearLocalSession, redirectToLogin]);

  const value = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    isLoggingOut,
    idToken,
    accessToken,
    refreshToken,
    setIdToken,
    setAccessToken,
    isPlatformSso,
    platformDept,
    platformOrgModules,
  }), [user, loading, login, logout, isLoggingOut, idToken, accessToken, refreshToken, isPlatformSso, platformDept, platformOrgModules]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
